<template>
  <div class="container py-4">
    <label class="titulo_pagina mb-5 d-block">Seleccionar un deporte</label>

    <!-- Estados -->
    <div v-if="loading" class="text-center py-5">
      <Loading v-if="loading" mensaje="Cargando deportes…" />
    </div>

    <div v-else-if="error" class="alert alert-danger" role="alert">
      <ErrorCarga :errorMsg="errorMsg" @retry="fetchSports" />
    </div>

    <div v-else-if="sports.length === 0" class="alert alert-info" role="alert">
      <SinDatos
        mensaje="No hay deportes habilitados por el momento. Hable con el administrador por favor."
      />
    </div>

    <!-- Grilla en filas de a 3; la última fila se centra si tiene 1 o 2 -->
    <div v-else>
      <div
        v-for="(row, idx) in rowsOfThree"
        :key="`row-${idx}`"
        class="row g-3 mb-3"
        :class="{ 'justify-content-lg-center': row.length < 3 }"
      >
        <div
          v-for="sport in row"
          :key="sport.id"
          class="col-12 col-sm-6 col-lg-4 d-flex"
        >
          <CardDeporte
            :descripcion_boton="sport.descripcion"
            :ruta_imagen="imgSrc(sport?.imagen) ?? FALLBACK"
            :ruta_imagen_alt="sport?.nombre || 'Deporte'"
            :imagen_error="onImgError"
            :action_click="() => selectSport(sport)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { apiGetDeportesActivos } from "@/api";
import { useRouter } from "vue-router";
import CardDeporte from "@/components/CardDeporte.vue";
import Loading from "@/components/Loading.vue";
import ErrorCarga from "@/components/ErrorCarga.vue";
import SinDatos from "@/components/SinDatos.vue";

const ctx = require.context(
  "@/assets/img/deportes",
  false,
  /\.(png|jpe?g|svg|webp|gif)$/i
);

// Mapa { "futbol.jpg": url, "tenis.png": url, ... } en minúsculas para buscar sin importar mayúsculas
const imageMap = ctx.keys().reduce((acc, key) => {
  const filename = key.replace("./", ""); // "./futbol.jpg" -> "futbol.jpg"
  acc[filename.toLowerCase()] = ctx(key); // webpack retorna la URL procesada
  return acc;
}, /** @type {Record<string, string>} */ ({}));

// Elegí el fallback disponible en tu carpeta (primero que encuentre)
const FALLBACK = imageMap["default.png"];

// util: ¿es URL absoluta?
function isAbsoluteUrl(u) {
  return /^https?:\/\//i.test(u) || /^data:/i.test(u);
}

// intenta matchear por nombre completo y, si no, por "baseName" (sin extensión)
function resolveImageByName(name) {
  if (!name) return null;

  // si viene URL absoluta desde la API, la usamos tal cual
  if (isAbsoluteUrl(name)) return name;

  // quedarnos sólo con el último segmento por si viene "deportes/futbol.jpg"
  const onlyName = String(name).split(/[\\/]/).pop() || "";
  const lower = onlyName.toLowerCase();

  // tratar cualquier "default" o "default.ext" como fallback
  if (/^default(\.\w+)?$/.test(lower)) return FALLBACK;

  // match exacto (con extensión)
  if (imageMap[lower]) return imageMap[lower];

  // match por basename (sin extensión), por si la API manda "futbol" o la extensión no coincide
  const base = lower.replace(/\.[^/.]+$/, "");
  for (const fname in imageMap) {
    const fbase = fname.replace(/\.[^/.]+$/, "");
    if (fbase === base) return imageMap[fname];
  }
  return null;
}

function imgSrc(filename) {
  return resolveImageByName(filename) || FALLBACK;
}

function onImgError(e) {
  if (e?.target && e.target.src !== FALLBACK) e.target.src = FALLBACK;
}

// Estado
const sports = ref([]);
const loading = ref(true);
const error = ref(false);
const errorMsg = ref("");

let controller = null;

async function fetchSports() {
  loading.value = true;
  error.value = false;
  errorMsg.value = "";

  // cancelar request anterior si aún sigue
  try {
    controller?.abort?.();
  } catch {}
  controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;

  try {
    const data = await apiGetDeportesActivos({ signal: controller?.signal });
    const lista = Array.isArray(data)
      ? data
      : data?.results ?? data?.items ?? [];

    // Normalización mínima
    sports.value = lista.map((s, i) => ({
      id: s.id_deporte ?? i,
      nombre: s.nombre ?? "Deporte",
      descripcion: s.descripcion ?? "",
      imagen: s.nombre_imagen ?? null,
      slug: s.slug ?? "",
    }));
  } catch (e) {
    console.error(e);
    error.value = true;
    errorMsg.value = "No se pudieron cargar los deportes.";
  } finally {
    loading.value = false;
  }
}

onMounted(fetchSports);

// Computado: filas de 3
const rowsOfThree = computed(() => {
  const out = [];
  for (let i = 0; i < sports.value.length; i += 3) {
    out.push(sports.value.slice(i, i + 3));
  }
  return out;
});

const router = useRouter();
function selectSport(sport) {
  router.push({ name: "reservas", query: { idDeporte: sport.id } });
}
</script>
